// Catalogue (S5 · 1.3) — the whole product library, staff-only, shared with the
// website. Signed off as a mockup on 2026-09-15 (`_briefs/aiw/mockups/`), built
// here to that shape.
//
// Three things it is for, in the order they were asked for:
//   1. find any product — one box that matches a colour name as well as a product
//   2. read it without hunting — grouped by category, one fixed frame per hero,
//      every pill shown
//   3. see the floor — click any image to enlarge it, with the colour rail beside it
//
// Costs come from the same resolution a Proposal uses (the effective-dated
// history), so what a person reads here and what a quote says cannot disagree.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { CATALOGUE_SPEC_FIELDS, type CatalogueCard, type CatalogueView, type SpecFamily } from "@artemis-group/aiw-contracts";
import { cn } from "@aiw/ui";
import { Page } from "../shell/Page";
import { useCatalogue } from "../hooks/useReads";
import { blobUrl } from "../lib/catalogue-images";
import { useT } from "../lib/i18n";

// ── what a product has to show ───────────────────────────────────────────────
// A photograph, a tile composed from its own swatches, or nothing. The middle
// case is labelled, because it is not a photograph.
const shotImages = (p: CatalogueCard) => p.colours.map((c) => blobUrl(c.image ?? c.swatch)).filter((x): x is string => !!x);
const shotKind = (p: CatalogueCard): "photo" | "built" | "none" => (p.hero ? "photo" : shotImages(p).length ? "built" : "none");
const needsPhoto = (p: CatalogueCard) => shotKind(p) === "none";
const needsCost = (p: CatalogueCard) => p.costFrom == null;
const money = (n: number) => "$" + n.toFixed(2);
const rangeText = (p: CatalogueCard) => {
  if (p.costFrom == null) return null;
  return p.costTo != null && p.costTo !== p.costFrom ? `${money(p.costFrom)}–${money(p.costTo).slice(1)}` : money(p.costFrom);
};

// Brand, not supplier: PACO supplies both PACO and National Select ranges under one ABN (Jacky, 8 Oct 2026).
const brandOf = (p: CatalogueCard) => p.brand ?? p.supplier;
const GROUPS = { type: "Category", subType: "Sub-type", brand: "Brand", none: "None" } as const;
type GroupKey = keyof typeof GROUPS;
const SORTS = { name: "A–Z", "price-asc": "Cost, low to high", "price-desc": "Cost, high to low", colours: "Most colours" } as const;
type SortKey = keyof typeof SORTS;

const chip = (on: boolean) =>
  cn("rounded-full border px-2.5 py-[3px] text-sm whitespace-nowrap transition-colors",
    on ? "border-rail bg-rail text-rail-foreground" : "bg-card text-muted-foreground hover:border-primary hover:text-foreground");
const selectCls = "h-9 rounded-md border bg-card px-2 text-sm";

export function Catalogue() {
  const {t}=useT();
  const list = useCatalogue();
  const [needle, setNeedle] = useState("");
  const [view, setView] = useState<"cards" | "list">("cards");
  const [group, setGroup] = useState<GroupKey>("type");
  const [sort, setSort] = useState<SortKey>("name");
  const [f, setF] = useState<{ type?: string; supplier?: string; use?: string; state?: string }>({});
  const [suppliersOpen, setSuppliersOpen] = useState(false);
  const [zoom, setZoom] = useState<{ id: string; i: number } | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const products = useMemo(() => list.data?.products ?? [], [list.data]);
  const counts = useMemo(() => ({
    products: products.length,
    colours: products.reduce((n, p) => n + p.colourCount, 0),
    website: products.filter((p) => p.website).length,
    photo: products.filter(needsPhoto).length,
    cost: products.filter(needsCost).length,
  }), [products]);
  const types = useMemo(() => [...new Set(products.map((p) => p.type))].sort(), [products]);
  const suppliers = useMemo(() => [...new Set(products.map(brandOf))].sort(), [products]);

  const rows = useMemo(() => {
    const n = needle.trim().toLowerCase();
    let out = products.filter((p) => !n || [p.name, p.customerName, brandOf(p), p.supplier, p.type, p.subType, p.slug ?? "", ...p.colours.map((c) => c.name)]
      .some((v) => v.toLowerCase().includes(n)));
    if (f.type) out = out.filter((p) => p.type === f.type);
    if (f.supplier) out = out.filter((p) => brandOf(p) === f.supplier);
    if (f.use === "residential") out = out.filter((p) => p.residential);
    if (f.use === "commercial") out = out.filter((p) => p.commercial);
    if (f.state === "photo") out = out.filter(needsPhoto);
    if (f.state === "cost") out = out.filter(needsCost);
    if (f.state === "noc") out = out.filter((p) => !p.colourCount);
    const by: Record<SortKey, (a: CatalogueCard, b: CatalogueCard) => number> = {
      name: (a, b) => a.customerName.localeCompare(b.customerName),
      "price-asc": (a, b) => (a.costFrom ?? Infinity) - (b.costFrom ?? Infinity) || a.customerName.localeCompare(b.customerName),
      "price-desc": (a, b) => (b.costFrom ?? -1) - (a.costFrom ?? -1) || a.customerName.localeCompare(b.customerName),
      colours: (a, b) => b.colourCount - a.colourCount || a.customerName.localeCompare(b.customerName),
    };
    return [...out].sort(by[sort]);
  }, [products, needle, f, sort]);

  // Grouping is the entrance: alphabetical over 172 rows never says where you are.
  const sections = useMemo(() => {
    if (group === "none") return [{ key: "", rows }];
    const map = new Map<string, CatalogueCard[]>();
    for (const p of rows) { const k = (group === "brand" ? brandOf(p) : p[group]) || "Other"; if (!map.has(k)) map.set(k, []); map.get(k)!.push(p); }
    return [...map.keys()].sort((a, b) => a.localeCompare(b)).map((k) => ({ key: k, rows: map.get(k)! }));
  }, [rows, group]);

  const filtered = needle.trim() !== "" || Object.values(f).some(Boolean);
  const zoomProduct = zoom ? products.find((p) => p.id === zoom.id) ?? null : null;
  const sheetProduct = sheet ? products.find((p) => p.id === sheet) ?? null : null;

  // "/" focuses the search, Escape closes, arrows walk the colours.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLSelectElement;
      if (e.key === "/" && !typing) { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === "Escape") { setZoom(null); setSheet(null); return; }
      if (zoom && zoomProduct?.colourCount) {
        if (e.key === "ArrowLeft") setZoom({ ...zoom, i: (zoom.i - 1 + zoomProduct.colourCount) % zoomProduct.colourCount });
        if (e.key === "ArrowRight") setZoom({ ...zoom, i: (zoom.i + 1) % zoomProduct.colourCount });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, zoomProduct]);

  const clearAll = () => { setNeedle(""); setF({}); };

  if (list.isPending) return <Page kicker="Operations" title="Catalogue"><p className="text-sm text-muted-foreground">Loading the catalogue…</p></Page>;
  if (list.isError) return <Page kicker="Operations" title="Catalogue"><p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{list.error.message}</p></Page>;

  return (
    <Page
      kicker="Operations"
      title="Catalogue"
      lede="One library, shared with the website. Costs are staff-only."
      actions={
        <div className="flex flex-wrap items-baseline gap-5">
          <a href="/settings?kind=artemis_services" className="rounded border px-3 py-2 text-sm text-primary hover:bg-accent">{t('service.standard')}</a>
          <Stat n={counts.products} label="products" />
          <Stat n={counts.colours} label="colours" />
          <Stat n={counts.website} label="on the website" />
          <Stat n={counts.photo} label="need a photograph" tone="text-warning" />
          <Stat n={counts.cost} label="need a cost" tone="text-warning" />
        </div>
      }
    >
      {/* find */}
      <div className="sticky top-0 z-20 -mx-1 mb-2 bg-background/95 px-1 pb-2 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              value={needle}
              onChange={(e) => setNeedle(e.target.value)}
              type="search"
              aria-label="Search the catalogue"
              placeholder="Find a product, a colour or a brand — try “blackbutt”, “hybrid”, “Signature”"
              className="h-11 w-full rounded-md border bg-card pl-10 pr-10 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 font-mono text-micro text-muted-foreground">/</kbd>
          </div>
          <div className="flex overflow-hidden rounded-md border bg-card">
            {(["cards", "list"] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v}
                className={cn("px-3 py-2 text-sm capitalize", view === v ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground hover:text-foreground")}>{v}</button>
            ))}
          </div>
          <select className={selectCls} value={group} onChange={(e) => setGroup(e.target.value as GroupKey)} aria-label="Group by">
            {(Object.keys(GROUPS) as GroupKey[]).map((k) => <option key={k} value={k}>Group: {GROUPS[k]}</option>)}
          </select>
          <select className={selectCls} value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort">
            {(Object.keys(SORTS) as SortKey[]).map((k) => <option key={k} value={k}>{SORTS[k]}</option>)}
          </select>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Facet label="Type">
            {types.map((t) => <button key={t} type="button" className={chip(f.type === t)} onClick={() => setF({ ...f, type: f.type === t ? undefined : t })}>{t}<Count n={products.filter((p) => p.type === t).length} /></button>)}
          </Facet>
          <Sep />
          <Facet label="Needs">
            <button type="button" className={chip(f.state === "photo")} onClick={() => setF({ ...f, state: f.state === "photo" ? undefined : "photo" })}>A photograph<Count n={counts.photo} /></button>
            <button type="button" className={chip(f.state === "cost")} onClick={() => setF({ ...f, state: f.state === "cost" ? undefined : "cost" })}>A cost<Count n={counts.cost} /></button>
            <button type="button" className={chip(f.state === "noc")} onClick={() => setF({ ...f, state: f.state === "noc" ? undefined : "noc" })}>No colours<Count n={products.filter((p) => !p.colourCount).length} /></button>
          </Facet>
          <Sep />
          <Facet label="Use">
            <button type="button" className={chip(f.use === "residential")} onClick={() => setF({ ...f, use: f.use === "residential" ? undefined : "residential" })}>Residential<Count n={products.filter((p) => p.residential).length} /></button>
            <button type="button" className={chip(f.use === "commercial")} onClick={() => setF({ ...f, use: f.use === "commercial" ? undefined : "commercial" })}>Commercial<Count n={products.filter((p) => p.commercial).length} /></button>
          </Facet>
          <Sep />
          {suppliersOpen && (
            <Facet label="Brand">
              {suppliers.map((s) => <button key={s} type="button" className={chip(f.supplier === s)} onClick={() => setF({ ...f, supplier: f.supplier === s ? undefined : s })}>{s.replace(/ (Floors|Flooring|Carpet)$/, "")}<Count n={products.filter((p) => brandOf(p) === s).length} /></button>)}
            </Facet>
          )}
          <button type="button" className={cn(chip(false), "border-dashed")} onClick={() => setSuppliersOpen((v) => !v)}>
            {suppliersOpen ? "Fewer brands ▴" : `Brands (${suppliers.length}) ▾`}
          </button>
        </div>
      </div>

      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="font-display text-sm">
          {rows.length === counts.products ? `All ${counts.products} products` : `${rows.length} of ${counts.products} products`}
          <span className="ml-1.5 text-muted-foreground">
            {needle.trim() && <>for “{needle.trim()}” </>}
            {group !== "none" && <>· grouped by {GROUPS[group].toLowerCase()}</>}
          </span>
        </div>
        {filtered && <button type="button" className="text-sm text-primary underline" onClick={clearAll}>Clear all filters</button>}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-card p-10 text-center">
          <div className="font-display text-lg">Nothing matches that</div>
          <p className="mt-1 text-sm text-muted-foreground">Try a colour name, a brand, or clear the filters.</p>
          <button type="button" className="mt-3 rounded-md border bg-muted px-3 py-1.5 text-sm" onClick={clearAll}>Clear all filters</button>
        </div>
      ) : view === "list" ? (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="grid grid-cols-[44px_minmax(190px,1.6fr)_minmax(130px,1fr)_92px_minmax(110px,1fr)_104px] items-center gap-3 border-b bg-muted px-3 py-2 font-mono text-micro uppercase tracking-wider text-muted-foreground">
            <span /><span>Product</span><span>Sub-type</span><span>Colours</span><span>Swatches</span><span className="text-right">Cost from</span>
          </div>
          {rows.map((p) => (
            <button key={p.id} type="button" onClick={() => setSheet(p.id)}
              className="grid w-full grid-cols-[44px_minmax(190px,1.6fr)_minmax(130px,1fr)_92px_minmax(110px,1fr)_104px] items-center gap-3 border-b px-3 py-1.5 text-left last:border-b-0 hover:bg-accent/40">
              <Thumb p={p} className="h-[34px] w-11 rounded" />
              <span className="min-w-0"><span className="block truncate text-sm font-medium">{p.customerName}</span><span className="block truncate text-micro text-muted-foreground">{brandOf(p)}</span></span>
              <span className="truncate text-micro text-muted-foreground">{p.subType}</span>
              <span className="text-micro text-muted-foreground">{p.colourCount} colours</span>
              <span className="flex gap-0.5">{p.colours.slice(0, 7).map((c, i) => {
                const s = blobUrl(c.swatch ?? c.image);
                return s ? <img key={i} src={s} alt="" loading="lazy" className="h-[18px] w-[18px] rounded-sm object-cover" /> : null;
              })}</span>
              <span className={cn("text-right font-mono text-sm", p.costFrom == null ? "text-warning" : "text-foreground")}>{rangeText(p) ?? "no cost"}</span>
            </button>
          ))}
        </div>
      ) : (
        sections.map(({ key, rows: groupRows }) => (
          <section key={key || "all"} className="mt-5 first:mt-0">
            {key && (
              <header className="mb-3 flex items-baseline gap-3 border-b-2 pb-1.5">
                <h2 className="font-display text-lg font-semibold">{key}</h2>
                <span className="font-mono text-micro text-muted-foreground">{groupRows.length} product{groupRows.length > 1 ? "s" : ""}</span>
                {groupRows.some(needsPhoto) && <span className="ml-auto text-micro text-warning">{groupRows.filter(needsPhoto).length} need a photograph</span>}
              </header>
            )}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(248px,1fr))] items-stretch gap-3">
              {groupRows.map((p) => <Card key={p.id} p={p} onZoom={() => setZoom({ id: p.id, i: 0 })} onOpen={() => setSheet(p.id)} />)}
            </div>
          </section>
        ))
      )}

      {sheetProduct && (
        <Sheet p={sheetProduct} onClose={() => setSheet(null)} onZoom={(i) => setZoom({ id: sheetProduct.id, i })} />
      )}
      {zoom && zoomProduct && (
        <Lightbox
          p={zoomProduct}
          i={zoom.i}
          onIndex={(i) => setZoom({ id: zoomProduct.id, i })}
          onClose={() => setZoom(null)}
        />
      )}
    </Page>
  );
}

const Stat = ({ n, label, tone }: { n: number; label: string; tone?: string }) => (
  <div className="text-right">
    <div className="font-display text-xl font-semibold leading-none">{tone ? <span className={tone}>{n}</span> : n}</div>
    <div className="text-micro whitespace-nowrap text-muted-foreground">{label}</div>
  </div>
);
const Count = ({ n }: { n: number }) => <span className="ml-1.5 font-mono text-micro opacity-60">{n}</span>;
const Facet = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-micro uppercase tracking-wider text-muted-foreground">{label}</span>
    {children}
  </div>
);
const Sep = () => <span className="h-5 w-px bg-border" />;

/** One fixed frame, three honest sources. */
function Thumb({ p, className }: { p: CatalogueCard; className?: string }) {
  const hero = blobUrl(p.hero);
  const imgs = shotImages(p);
  if (hero) return <img src={hero} alt="" loading="lazy" className={cn("object-cover", className)} />;
  if (imgs.length >= 2) return (
    <span className={cn("grid grid-cols-2 gap-px overflow-hidden bg-border", className)}>
      {imgs.slice(0, 4).map((s, i) => <img key={i} src={s} alt="" loading="lazy" className="h-full w-full object-cover" />)}
    </span>
  );
  if (imgs.length === 1) return <img src={imgs[0]} alt="" loading="lazy" className={cn("object-cover", className)} />;
  return <span className={cn("grid place-items-center bg-muted text-micro text-muted-foreground", className)}>none</span>;
}

function Card({ p, onZoom, onOpen }: { p: CatalogueCard; onZoom: () => void; onOpen: () => void }) {
  const hero = blobUrl(p.hero);
  const imgs = shotImages(p);
  const kind = shotKind(p);
  const tags: { label: string; tone?: string }[] = [
    p.colourCount ? { label: `${p.colourCount} colours`, tone: "bg-accent text-accent-foreground" } : { label: "No colours" },
    ...(p.residential ? [{ label: "Residential", tone: "bg-success/15 text-success" }] : []),
    ...(p.commercial ? [{ label: "Commercial" }] : []),
    ...(p.docs ? [{ label: `${p.docs} doc${p.docs > 1 ? "s" : ""}` }] : []),
    ...(p.website ? [] : [{ label: "Off website", tone: "bg-warning/15 text-warning" }]),
    ...(needsPhoto(p) ? [{ label: "No photo", tone: "bg-warning/15 text-warning" }] : []),
  ];
  return (
    <article className="flex flex-col overflow-hidden rounded-lg border bg-card">
      <button type="button" onClick={onZoom} aria-label={`Enlarge ${p.customerName}`} className="group relative block aspect-[4/3] w-full overflow-hidden border-b bg-muted">
        {hero ? <img src={hero} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" /> : null}
        {!hero && imgs.length >= 2 && (
          <span className="absolute inset-0 grid grid-cols-2 gap-px bg-border">
            {imgs.slice(0, 4).map((s, i) => <img key={i} src={s} alt="" loading="lazy" className="h-full w-full object-cover" />)}
          </span>
        )}
        {!hero && imgs.length === 1 && <img src={imgs[0]} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />}
        {/* Only the built case needs a label: the blank tile already says "No
            photograph" across its middle, and saying it twice reads as a bug. */}
        {kind === "built" && (
          <span className="absolute left-2 top-2 rounded border bg-card/95 px-1.5 py-px text-micro text-muted-foreground">Built from swatches</span>
        )}
        <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-rail/90 px-2 py-0.5 text-micro text-rail-foreground opacity-0 transition-opacity group-hover:opacity-100">
          <ZoomIn className="h-3 w-3" />Enlarge
        </span>
        {kind === "none" && (
          <span className="absolute inset-0 grid place-items-center gap-1 text-center text-micro text-muted-foreground">
            <span>No photograph</span><span className="opacity-80">{brandOf(p)} · {p.type}</span>
          </span>
        )}
      </button>
      <div className="grid flex-1 grid-rows-[2.6em_auto_26px_auto_auto] content-start gap-1.5 p-2.5">
        <button type="button" onClick={onOpen} title={p.customerName} className="line-clamp-2 text-left font-display text-sm font-semibold leading-snug hover:text-primary hover:underline">{p.customerName}</button>
        <div className="truncate text-micro text-muted-foreground">{brandOf(p)} · {p.subType} · {p.type}</div>
        <div className="flex items-center gap-1 overflow-hidden">
          {p.colours.slice(0, 8).map((c, i) => {
            const s = blobUrl(c.swatch ?? c.image);
            return s ? <img key={i} src={s} alt="" loading="lazy" className="h-[22px] w-[22px] shrink-0 rounded-sm border object-cover" /> : null;
          })}
          {p.colourCount > 8 && <span className="text-micro text-muted-foreground">+{p.colourCount - 8}</span>}
        </div>
        <div className="flex flex-wrap content-start gap-1">
          {tags.map((t, i) => <span key={i} className={cn("rounded-full border px-1.5 py-px text-micro whitespace-nowrap", t.tone ?? "bg-muted text-muted-foreground")}>{t.label}</span>)}
        </div>
        {/* pinned to the bottom, so the money lines up across a grid row even when
            one card's pills take two lines */}
        <div className="mt-auto flex items-baseline gap-2 border-t pt-2">
          {p.costFrom == null ? (
            <>
              <span className="rounded bg-warning/15 px-1.5 py-px text-micro text-warning">No cost</span>
              <span className="truncate text-micro text-muted-foreground">{p.issues[0] ?? "needs review"}</span>
            </>
          ) : (
            <>
              <span className="rounded bg-gold/15 px-1.5 py-px font-mono text-sm text-gold">{rangeText(p)}</span>
              <span className="truncate text-micro text-muted-foreground">per {p.unit}{p.basis ? ` · ${p.basis}` : ""}</span>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function Sheet({ p, onClose, onZoom }: { p: CatalogueCard; onClose: () => void; onZoom: (i: number) => void }) {
  const {t}=useT();
  const hero = blobUrl(p.hero);
  const imgs = shotImages(p);
  const row = (k: string, v: React.ReactNode) => v == null ? null : (<><div className="text-muted-foreground">{k}</div><div className="text-right font-medium">{v}</div></>);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-foreground/40" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={p.customerName} className="h-full w-[min(560px,94vw)] overflow-auto rounded-l-xl border-l bg-card">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-card px-4 py-3">
          <div>
            <h2 className="font-display text-xl font-semibold">{p.customerName}</h2>
            <div className="mt-0.5 text-micro text-muted-foreground">{brandOf(p)} · {p.type} / {p.subType}{p.slug ? ` · ${p.slug}` : ""}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-md border bg-muted px-2.5 py-1 text-sm">Close ✕</button>
        </header>
        <div className="flex flex-col gap-4 p-4">
          <div>
            {hero && <img src={hero} alt="" onClick={() => onZoom(0)} className="aspect-[4/3] w-full cursor-zoom-in rounded-lg border object-cover" />}
            {!hero && imgs.length > 0 && <img src={imgs[0]} alt="" onClick={() => onZoom(0)} className="aspect-[4/3] w-full cursor-zoom-in rounded-lg border object-cover" />}
            {!hero && (
              <div className="mt-2 rounded border border-l-4 border-warning/40 border-l-warning bg-warning/5 p-2.5 text-sm text-warning">
                No photograph for this product.{imgs.length ? " The image above is built from its colour swatches." : " None of its colours has an image either."}
              </div>
            )}
          </div>
          {p.blurb && <Section title="Website description"><p className="text-sm text-muted-foreground">{p.blurb}</p></Section>}
          <Section title="Cost">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border text-sm">
              {row("Cost from", p.costFrom == null ? <span className="text-warning">No cost</span> : `${money(p.costFrom)} per ${p.unit}`)}
              {row("Cost to", p.costTo != null && p.costTo !== p.costFrom ? `${money(p.costTo)} per ${p.unit}` : p.costFrom != null ? "Same for every colour" : null)}
              {row("Price basis", p.basis)}
              {row("Effective", p.effectiveDate)}
              {row("Installation", p.rule ?? <span className="text-warning">Needs matching</span>)}
            </div>
            <p className="mt-2 rounded border border-l-4 border-accent border-l-primary bg-accent/40 p-2.5 text-sm">Cost only — the sell price comes from the pricing rules when this goes on a Proposal.</p>
          </Section>
          {p.issues.length > 0 && <div className="rounded border border-warning/40 bg-warning/5 p-2.5 text-sm text-warning">{p.issues.join(" · ")}</div>}
          <Section title="Specification">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border text-sm">
              {row("Type", p.type)}{row("Sub-type", p.subType)}
              {row("Width", p.width == null ? null : p.type === "Carpet" ? `${p.width} m roll` : `${p.width} mm`)}
              {row("Pattern", p.pattern)}{row("Sold by", p.uom)}{row("Documents", String(p.docs))}
            </div>
            {/* Every field the family's spec table records (25 Oct 2026). A gap reads "Not recorded" rather than vanishing. */}
            {!!p.specFamily && !!p.specs?.length && (() => {
              const fields = CATALOGUE_SPEC_FIELDS[p.specFamily as SpecFamily] ?? [], missing = p.specs.filter((s) => s.value === null).length;
              const shown = (key: string, v: string | number | boolean) => {
                if (typeof v === "boolean") return t(v ? "catalogue.spec.yes" : "catalogue.spec.no");
                const unit = fields.find((f) => f.key === key)?.unit;
                if (typeof v !== "number" || !unit) return String(v);
                return unit === "years" ? t("catalogue.spec.years", { n: String(v) }) : `${v} ${unit}`;
              };
              return <>
                {missing > 0 && <p className="mt-2 text-sm text-warning">{t("catalogue.spec.missing", { count: String(missing) })}</p>}
                <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border text-sm">
                  {p.specs.map((s) => <Fragment key={s.key}>
                    <div className="text-muted-foreground">{t(`catalogue.spec.${s.key}` as Parameters<typeof t>[0])}</div>
                    <div className={cn("text-right font-medium", s.value === null && "text-warning")}>{s.value === null ? t("catalogue.spec.notRecorded") : shown(s.key, s.value)}</div>
                  </Fragment>)}
                </div>
              </>;
            })()}
          </Section>
          {!!p.documentLinks?.length && <Section title={t('catalogue.documents')}>
            <div className="flex flex-col gap-2">
              {p.documentLinks.map(d=><a key={d.url} href={d.url} target="_blank" rel="noopener noreferrer" className="rounded-md border px-3 py-2 text-sm text-primary hover:bg-accent" aria-label={t('catalogue.openPdf',{title:d.title})}>{d.title} · PDF ↗</a>)}
            </div>
          </Section>}
          <Section title="Availability">
            <div className="flex flex-wrap gap-1.5 text-micro">
              {p.residential && <span className="rounded-full border border-success/30 bg-success/15 px-2 py-px text-success">Residential</span>}
              {p.commercial && <span className="rounded-full border bg-muted px-2 py-px">Commercial</span>}
              {p.featured && <span className="rounded-full border border-gold/30 bg-gold/15 px-2 py-px text-gold">Featured</span>}
              <span className={cn("rounded-full border px-2 py-px", p.website ? "border-primary/30 bg-accent text-accent-foreground" : "border-warning/40 bg-warning/15 text-warning")}>{p.website ? "On the website" : "Not on the website"}</span>
              {needsPhoto(p) && <span className="rounded-full border border-warning/40 bg-warning/15 px-2 py-px text-warning">Needs a photograph</span>}
            </div>
          </Section>
          <Section title={`Colours (${p.colourCount})`}>
            {p.colourCount === 0 ? <p className="text-sm text-muted-foreground">No colours recorded.</p> : (
              <div className="flex max-h-80 flex-col gap-0.5 overflow-auto rounded-lg border p-1.5">
                {p.colours.map((c, i) => {
                  const s = blobUrl(c.swatch ?? c.image);
                  return (
                    <button key={i} type="button" onClick={() => onZoom(i)} className="flex items-center gap-2.5 rounded px-1.5 py-1 text-left text-sm hover:bg-muted">
                      {s ? <img src={s} alt="" loading="lazy" className="h-[30px] w-[30px] rounded-sm object-cover" /> : <span className="h-[30px] w-[30px] rounded-sm bg-muted" />}
                      {c.name}
                      {c.specialOrder && <span className="ml-auto rounded-full border border-warning/40 bg-warning/15 px-2 py-px text-micro text-warning">{t('catalogue.specialOrder')}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h3 className="mb-2 font-mono text-micro uppercase tracking-wider text-muted-foreground">{title}</h3>
    {children}
  </section>
);

function Lightbox({ p, i, onIndex, onClose }: { p: CatalogueCard; i: number; onIndex: (i: number) => void; onClose: () => void }) {
  const c = p.colours[i];
  const big = blobUrl(c?.image ?? c?.swatch) ?? blobUrl(p.hero);
  const facts: [string, string][] = [
    ["Brand", brandOf(p)],
    ["Supplier", p.supplier],
    ["Type", `${p.type} · ${p.subType}`],
    ["Cost from", p.costFrom == null ? "No cost" : `${money(p.costFrom)} per ${p.unit}`],
    ...(p.costTo != null && p.costTo !== p.costFrom ? [["Cost to", `${money(p.costTo)} per ${p.unit}`] as [string, string]] : []),
    ...(p.basis ? [["Price basis", p.basis] as [string, string]] : []),
    ...(p.effectiveDate ? [["Effective", p.effectiveDate] as [string, string]] : []),
    ...(p.width != null ? [["Width", p.type === "Carpet" ? `${p.width} m roll` : `${p.width} mm`] as [string, string]] : []),
    ["Photograph", p.hero ? "Yes" : shotImages(p).length ? "Built from swatches" : "Missing"],
    ["On the website", p.website ? "Yes" : "No"],
    ["Documents", String(p.docs)],
  ];
  return (
    <div className="fixed inset-0 z-[60] bg-rail/95" role="dialog" aria-modal="true" aria-label={`${p.customerName} image`}>
      <button type="button" onClick={onClose} className="absolute right-4 top-4 z-10 rounded-md border border-white/30 bg-[#083A44] px-3 py-1.5 text-sm text-white shadow-lg">Close ✕</button>
      <div className="grid h-full grid-cols-[1fr_272px]">
        <div className="flex min-w-0 flex-col justify-center gap-3.5 p-6">
          <div className="grid min-h-0 flex-1 place-items-center">
            {big ? <img src={big} alt={`${p.customerName}${c ? " — " + c.name : ""}`} className="max-h-[72vh] max-w-full rounded-lg bg-muted shadow-2xl" /> : <span className="text-sm text-rail-muted">No image for this product.</span>}
          </div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-white">{c ? c.name : p.customerName}</h2>
              <div className="mt-0.5 text-sm text-rail-muted">{p.customerName} · {brandOf(p)} · {p.type} / {p.subType}</div>
            </div>
            <div className="flex gap-1.5">
              <button type="button" disabled={!p.colourCount} onClick={() => onIndex((i - 1 + p.colourCount) % p.colourCount)} className="flex items-center gap-1 rounded border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm text-white hover:bg-white/20 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" />Prev</button>
              <button type="button" disabled={!p.colourCount} onClick={() => onIndex((i + 1) % p.colourCount)} className="flex items-center gap-1 rounded border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm text-white hover:bg-white/20 disabled:opacity-40">Next<ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>
        <div className="flex min-h-0 flex-col bg-card">
          <h3 className="px-3.5 pb-2 pt-3.5 font-mono text-micro uppercase tracking-wider text-muted-foreground">{p.colourCount ? `Colours (${p.colourCount})` : "Colours"}</h3>
          <div className="flex flex-1 flex-col gap-0.5 overflow-auto px-2.5 pb-3">
            {p.colourCount === 0 ? <p className="p-2 text-sm text-muted-foreground">No colours recorded for this product.</p> : p.colours.map((x, n) => {
              const s = blobUrl(x.swatch ?? x.image);
              return (
                <button key={n} type="button" onClick={() => onIndex(n)} aria-current={n === i}
                  className={cn("flex w-full items-center gap-2.5 rounded border px-2 py-1 text-left text-sm", n === i ? "border-primary bg-accent" : "border-transparent hover:bg-muted")}>
                  {s ? <img src={s} alt="" loading="lazy" className="h-[34px] w-[34px] rounded-sm object-cover" /> : <span className="h-[34px] w-[34px] rounded-sm bg-muted" />}
                  {x.name}
                </button>
              );
            })}
          </div>
          <dl className="flex flex-col gap-1.5 border-t px-3.5 py-3 text-sm">
            {facts.map(([k, v]) => <div key={k} className="flex justify-between gap-2.5"><dt className="text-muted-foreground">{k}</dt><dd className="text-right">{v}</dd></div>)}
          </dl>
        </div>
      </div>
    </div>
  );
}

export type { CatalogueView };
